/**
 * Feb 28, 2026 · Sales Order Reconciliation
 *
 * Full-lifecycle position for every SO/ESO line:
 *   Order Qty · Dispatched Qty · Excess Produced · Stock Qty · Balance Pending
 * Filter by customer or product code. Total summary row at the bottom.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ArrowsClockwiseIcon, DownloadSimpleIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function SalesOrderReconciliation({ defaultSource = "ALL", lockSource = false, title = "Sales Order Reconciliation", subtitle = "Order Qty · Dispatched · Excess Produced · FG Stock · Balance Pending — per SO / ESO line.", testIdPrefix = "so-reconciliation" }) {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState("");
  const [product, setProduct] = useState("");
  const [source, setSource] = useState(defaultSource);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reconciliation/sales-orders");
      setData(data || { rows: [], totals: {} });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const customers = useMemo(() => Array.from(new Set((data.rows || []).map(r => r.customer).filter(Boolean))).sort(), [data]);
  const products  = useMemo(() => Array.from(new Set((data.rows || []).map(r => r.product_code).filter(Boolean))).sort(), [data]);

  const filtered = useMemo(() => {
    return (data.rows || []).filter(r => {
      if (source !== "ALL" && r.source !== source) return false;
      if (customer && r.customer !== customer) return false;
      if (product && r.product_code !== product) return false;
      return true;
    });
  }, [data.rows, customer, product, source]);

  const totals = useMemo(() => filtered.reduce((a, r) => ({
    lines: a.lines + 1,
    order_qty: a.order_qty + (Number(r.order_qty) || 0),
    dispatched_qty: a.dispatched_qty + (Number(r.dispatched_qty) || 0),
    excess_produced: a.excess_produced + (Number(r.excess_produced) || 0),
    excess_supplied: a.excess_supplied + (Number(r.excess_supplied) || 0),
    stock_qty: a.stock_qty + (Number(r.stock_qty) || 0),
    balance_pending: a.balance_pending + (Number(r.balance_pending) || 0),
  }), { lines: 0, order_qty: 0, dispatched_qty: 0, excess_produced: 0, excess_supplied: 0, stock_qty: 0, balance_pending: 0 }), [filtered]);

  const exportCsv = () => {
    exportToCSV("sales-order-reconciliation.csv", filtered.map(r => ({
      source: r.source, order_no: r.order_no, customer: r.customer, product_code: r.product_code,
      style_no: r.style_no, order_qty: r.order_qty, dispatched_qty: r.dispatched_qty,
      excess_produced: r.excess_produced, excess_supplied: r.excess_supplied, stock_qty: r.stock_qty, balance_pending: r.balance_pending,
      delivery_date: r.delivery_date || "",
    })), [
      { key: "source", label: "Source" }, { key: "order_no", label: "Order No" },
      { key: "customer", label: "Customer" }, { key: "product_code", label: "Product Code" },
      { key: "style_no", label: "Style No" },
      { key: "order_qty", label: "Order Qty" }, { key: "dispatched_qty", label: "Dispatched" },
      { key: "excess_produced", label: "Excess Produced" },
      { key: "excess_supplied", label: "Excess Supplied" },
      { key: "stock_qty", label: "Stock Qty" },
      { key: "balance_pending", label: "Balance Pending" },
      { key: "delivery_date", label: "Req Delivery Date" },
    ]);
  };

  return (
    <div className="p-6 space-y-5" data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-zinc-500 mb-1">Reports</div>
          <h1 className="font-display text-2xl">{title}</h1>
          <p className="text-xs text-zinc-400 mt-1">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="recon-refresh" onClick={load} disabled={loading}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2">
            <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
          </button>
          <button data-testid="recon-export" onClick={exportCsv}
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2">
            <DownloadSimpleIcon size={14} weight="bold" />Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="tactical-card p-3 flex items-end gap-3 flex-wrap" data-testid="recon-filters">
        <div>
          <div className="overline text-[10px] mb-1">Source</div>
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
            {["ALL", "SO", "ESO"].map(v => (
              <button key={v} data-testid={`recon-src-${v}`}
                onClick={() => !lockSource && setSource(v)}
                disabled={lockSource && v !== defaultSource}
                className={`px-3 py-2 text-xs font-semibold tracking-wide ${source === v ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"} ${lockSource && v !== defaultSource ? "opacity-30 cursor-not-allowed" : ""}`}
              >{v}</button>
            ))}
          </div>
        </div>
        <div className="min-w-[220px]">
          <div className="overline text-[10px] mb-1">Customer</div>
          <div className="relative">
            <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="recon-customer" list="recon-customers" value={customer}
              onChange={e => setCustomer(e.target.value)} placeholder="All customers"
              className="w-full h-9 pl-7 pr-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]" />
            <datalist id="recon-customers">{customers.map(c => <option key={c} value={c} />)}</datalist>
          </div>
        </div>
        <div className="min-w-[260px]">
          <div className="overline text-[10px] mb-1">Product Code</div>
          <div className="relative">
            <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input data-testid="recon-product" list="recon-products" value={product}
              onChange={e => setProduct(e.target.value)} placeholder="All product codes"
              className="w-full h-9 pl-7 pr-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)] font-mono" />
            <datalist id="recon-products">{products.map(p => <option key={p} value={p} />)}</datalist>
          </div>
        </div>
        {(customer || product || source !== "ALL") && (
          <button data-testid="recon-filters-clear" onClick={() => { setCustomer(""); setProduct(""); setSource("ALL"); }}
            className="h-9 px-3 text-xs text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear filters</button>
        )}
        <div className="ml-auto text-[11px] text-zinc-500 font-mono">{filtered.length} of {(data.rows || []).length} lines</div>
      </div>

      {/* Table */}
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)] sticky top-0 z-20 bg-[var(--bg-1)]">
              <th className="text-left px-3 py-3">Source</th>
              <th className="text-left px-3 py-3">Order No</th>
              <th className="text-left px-3 py-3">Customer</th>
              <th className="text-left px-3 py-3">Product Code</th>
              <th className="text-left px-3 py-3">Style</th>
              <th className="text-right px-3 py-3">Order Qty</th>
              <th className="text-right px-3 py-3">Dispatched</th>
              <th className="text-right px-3 py-3">Excess Produced</th>
              <th className="text-right px-3 py-3" title="Actual Dispatched − Order Qty (per line, not netted against pending on other lines)">Excess Supplied</th>
              <th className="text-right px-3 py-3">Stock Qty</th>
              <th className="text-right px-3 py-3">Balance Pending</th>
              <th className="text-left px-3 py-3">Req Delivery</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="text-center py-12 text-zinc-500">{loading ? "Loading…" : "No SO / ESO lines match the filters"}</td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={`${r.order_id}-${r.line_id || i}`} className="border-b border-[var(--border)]/40 hover:bg-[var(--bg-2)]/40"
                  data-testid={`recon-row-${r.order_no}-${r.product_code}`}>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-mono font-semibold ${r.source === "SO" ? "bg-blue-500/15 text-blue-300" : "bg-sky-500/15 text-sky-300"}`}>{r.source}</span>
                </td>
                <td className="px-3 py-2 font-mono">{r.order_no}</td>
                <td className="px-3 py-2 text-zinc-300 text-xs">{r.customer}</td>
                <td className="px-3 py-2 font-mono text-zinc-300 text-xs truncate max-w-[280px]" title={r.product_code}>{r.product_code || "—"}</td>
                <td className="px-3 py-2 font-mono text-zinc-400 text-xs">{r.style_no || "—"}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-white bg-pink-500/5">{fmt(r.order_qty)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-300 bg-emerald-500/5">{fmt(r.dispatched_qty)}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-300 bg-amber-500/5">{fmt(r.excess_produced)}</td>
                <td className={`px-3 py-2 text-right font-mono ${(Number(r.excess_supplied) || 0) > 0 ? "text-orange-300 bg-orange-500/10 font-semibold" : "text-zinc-500"}`} data-testid={`recon-excess-supplied-${r.order_no}-${r.product_code}`}>{fmt(r.excess_supplied || 0)}</td>
                <td className="px-3 py-2 text-right font-mono text-purple-300 bg-purple-500/5">{fmt(r.stock_qty)}</td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${r.balance_pending > 0 ? "text-amber-300 bg-amber-500/10" : "text-emerald-400 bg-emerald-500/10"}`}>{fmt(r.balance_pending)}</td>
                <td className={`px-3 py-2 font-mono text-xs ${r.delivery_date && r.delivery_date < new Date().toISOString().slice(0,10) && r.balance_pending > 0 ? "text-red-400 font-semibold" : "text-zinc-400"}`} title={r.delivery_date ? "Item-wise Required Delivery Date" : "No line-level date · falls back to order-level"}>{r.delivery_date || "—"}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[var(--accent)]/40 bg-[var(--bg-2)]/60 font-semibold" data-testid="recon-totals">
                <td className="px-3 py-3 uppercase text-[10px] tracking-widest text-[var(--accent)]" colSpan={5}>Total · {totals.lines} lines</td>
                <td className="px-3 py-3 text-right font-mono">{fmt(totals.order_qty)}</td>
                <td className="px-3 py-3 text-right font-mono text-emerald-300">{fmt(totals.dispatched_qty)}</td>
                <td className="px-3 py-3 text-right font-mono text-amber-300">{fmt(totals.excess_produced)}</td>
                <td className="px-3 py-3 text-right font-mono text-orange-300" data-testid="recon-total-excess-supplied">{fmt(totals.excess_supplied)}</td>
                <td className="px-3 py-3 text-right font-mono text-purple-300">{fmt(totals.stock_qty)}</td>
                <td className="px-3 py-3 text-right font-mono text-amber-300">{fmt(totals.balance_pending)}</td>
                <td className="px-3 py-3 font-mono text-zinc-500 text-xs">—</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
